"use client";
import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import { useParams } from "@/hooks";
import {
  fetchAsaasPaymentsOverview,
  refundAsaasPayment,
  type PaymentsOverviewQuery,
} from "@/store/modules/asaasPayments/thunks";
import { forceClearOverviewLoading } from "@/store/modules/asaasPayments/slice";

const DEFAULT_LIMIT = 100;

export const useAsaasPayments = () => {
  const dispatch: AppDispatch = useDispatch();
  const { params, updateParams } = useParams();
  const { overview, mutating } = useSelector(
    (s: RootState) =>
      (s as any).asaasPayments || { overview: {}, mutating: false },
  );

  // Estado para o id do estabelecimento
  const [establishmentId, setEstablishmentId] = useState<string>("");
  const [email, setEmail] = useState("");
  const [debounced, setDebounced] = useState(email);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(email), 300);
    return () => clearTimeout(id);
  }, [email]);

  useEffect(() => {
    // Monta a query exatamente como esperado na rota
    const query: PaymentsOverviewQuery = {
      establishmentId: establishmentId || undefined,
      email: debounced || undefined,
      page: params.current_page,
      limit: DEFAULT_LIMIT,
    };
    dispatch(fetchAsaasPaymentsOverview(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debounced,
    establishmentId,
    params.current_page,
    params.per_page,
    dispatch,
  ]);

  useEffect(() => {
    if (overview?.loading) {
      const id = setTimeout(() => dispatch(forceClearOverviewLoading()), 12000);
      return () => clearTimeout(id);
    }
  }, [overview?.loading, dispatch]);

  const handlePage = useCallback(
    (page: number) => updateParams({ current_page: page }),
    [updateParams],
  );
  const handlePerPage = useCallback(
    (per: number) => updateParams({ per_page: per, current_page: 1 }),
    [updateParams],
  );

  const refund = useCallback(
    (paymentId: string, establishmentId?: number | string) =>
      dispatch(refundAsaasPayment({ paymentId, establishmentId })),
    [dispatch],
  );

  return {
    establishmentId,
    setEstablishmentId,
    email,
    setEmail,
    overview,
    mutating,
    handlePage,
    handlePerPage,
    refund,
    currentPage: overview?.pagination?.current_page || 1,
    totalPages: overview?.pagination?.last_page || 1,
    refresh: () =>
      dispatch(
        fetchAsaasPaymentsOverview({
          establishmentId: establishmentId || undefined,
          email: debounced || undefined,
          page: params.current_page,
          limit: params.per_page || DEFAULT_LIMIT,
        }),
      ),
  };
};

export default useAsaasPayments;
